import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

export const Header = () => {
  const { t, isRTL } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (isHomePage) {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setIsMobileMenuOpen(false);
      }
    } else {
      window.location.href = `/#${id}`;
    }
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        isScrolled
          ? 'backdrop-blur-md bg-white/80 border-b border-white/20 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0" data-testid="logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_cf89158a-bcc4-4183-9507-642ba4ecfd15/artifacts/wcokteot_rt.png" 
              alt="Kaufmanns Consulting"
              className="h-8 sm:h-10 md:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <button
              onClick={() => scrollToSection('packages')}
              className="text-slate-600 hover:text-brand font-medium transition-colors"
              data-testid="nav-packages"
            >
              {t('nav.packages')}
            </button>
            <button
              onClick={() => scrollToSection('how-it-works')}
              className="text-slate-600 hover:text-brand font-medium transition-colors"
              data-testid="nav-how-it-works"
            >
              {t('nav.howItWorks')}
            </button>
            <Link
              to="/about"
              className="text-slate-600 hover:text-brand font-medium transition-colors"
              data-testid="nav-about"
            >
              {t('nav.about')}
            </Link>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-slate-600 hover:text-brand font-medium transition-colors"
              data-testid="nav-contact"
            >
              {t('nav.contact')}
            </button>
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-2 sm:gap-4">
            <LanguageSwitcher />
            
            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 -mr-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-slate-700" />
              ) : (
                <Menu className="w-6 h-6 text-slate-700" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-100 bg-white/95 backdrop-blur-sm" data-testid="mobile-menu">
            <nav className="flex flex-col gap-2">
              <button
                onClick={() => scrollToSection('packages')}
                className="text-slate-600 hover:text-brand hover:bg-slate-50 font-medium text-left py-3 px-2 rounded-lg transition-colors"
              >
                {t('nav.packages')}
              </button>
              <button
                onClick={() => scrollToSection('how-it-works')}
                className="text-slate-600 hover:text-brand hover:bg-slate-50 font-medium text-left py-3 px-2 rounded-lg transition-colors"
              >
                {t('nav.howItWorks')}
              </button>
              <Link
                to="/about"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-slate-600 hover:text-brand hover:bg-slate-50 font-medium text-left py-3 px-2 rounded-lg transition-colors"
              >
                {t('nav.about')}
              </Link>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-slate-600 hover:text-brand hover:bg-slate-50 font-medium text-left py-3 px-2 rounded-lg transition-colors"
              >
                {t('nav.contact')}
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
